import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Plus } from 'lucide-react';
import type { PollQuestion } from '../types';

interface PollQuestionFormProps {
  question: PollQuestion;
  index: number;
  onChange: (updated: PollQuestion) => void;
  onRemove: () => void;
}

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'free_text', label: 'Free Text' },
];

export function PollQuestionForm({ question, index, onChange, onRemove }: PollQuestionFormProps) {
  const isChoiceType = question.type === 'single_choice' || question.type === 'multiple_choice';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Question {index + 1}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor={`q-text-${question.questionId}`}>Question Text</Label>
          <Input
            id={`q-text-${question.questionId}`}
            value={question.text}
            onChange={(e) => onChange({ ...question, text: e.target.value })}
            placeholder="Enter your question..."
          />
        </div>

        <div>
          <Label htmlFor={`q-type-${question.questionId}`}>Type</Label>
          <select
            id={`q-type-${question.questionId}`}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={question.type}
            onChange={(e) => {
              const newType = e.target.value;
              const updated = { ...question, type: newType };
              if (
                (newType === 'single_choice' || newType === 'multiple_choice') &&
                !question.options?.length
              ) {
                updated.options = ['', ''];
              } else if (newType === 'free_text') {
                updated.options = undefined;
              }
              onChange(updated);
            }}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {isChoiceType && (
          <div className="space-y-2">
            <Label>Options</Label>
            {(question.options || []).map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={opt}
                  onChange={(e) => {
                    const newOptions = [...(question.options || [])];
                    newOptions[i] = e.target.value;
                    onChange({ ...question, options: newOptions });
                  }}
                  placeholder={`Option ${i + 1}`}
                />
                {(question.options?.length ?? 0) > 2 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newOptions = (question.options || []).filter((_, j) => j !== i);
                      onChange({ ...question, options: newOptions });
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onChange({ ...question, options: [...(question.options || []), ''] });
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Option
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id={`q-required-${question.questionId}`}
            checked={question.required}
            onCheckedChange={(checked) => onChange({ ...question, required: checked === true })}
          />
          <Label htmlFor={`q-required-${question.questionId}`} className="text-sm">
            Required
          </Label>
        </div>
      </CardContent>
    </Card>
  );
}
